import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Switch,
  UseModalProps,
  VStack,
} from '@chakra-ui/react'
import { Org_Plan_Type_Enum, useAdminUpdateOrgPlanMutation } from '@gql'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props extends UseModalProps {
  org: {
    id: string
    name: string
    plan: Org_Plan_Type_Enum
    planEndsAt?: string | null
    disabledAt?: string | null
    whiteboardEnabled: boolean
  }
  onUpdated?(): void
}

// Les 4 plans, dans l'ordre croissant : c'est celui d'une grille tarifaire, pas
// l'ordre alphabétique que renverrait l'enum.
export const PLAN_ORDER: Org_Plan_Type_Enum[] = [
  Org_Plan_Type_Enum.Free,
  Org_Plan_Type_Enum.CartoSolo,
  Org_Plan_Type_Enum.CartoGroov,
]

// Clés littérales plutôt qu'une clé construite : un plan ajouté à l'enum sans sa
// traduction casse la compilation au lieu d'afficher la clé brute à l'écran.
// `as const` garde les clés littérales, que le typage strict de `t()` exige ;
// `satisfies` impose que les 3 plans y figurent.
export const PLAN_LABEL_KEY = {
  [Org_Plan_Type_Enum.Free]: 'SuperAdmin.orgs.plans.Free',
  [Org_Plan_Type_Enum.CartoSolo]: 'SuperAdmin.orgs.plans.CartoSolo',
  [Org_Plan_Type_Enum.CartoGroov]: 'SuperAdmin.orgs.plans.CartoGroov',
} as const satisfies Record<Org_Plan_Type_Enum, string>

// timestamptz → la valeur `YYYY-MM-DD` qu'attend <input type="date">.
function toDateInput(value?: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

export default function AdminOrgPlanModal({
  org,
  onUpdated,
  ...modalProps
}: Props) {
  const { t } = useTranslation()
  const [updateOrgPlan, { loading }] = useAdminUpdateOrgPlanMutation()

  const [plan, setPlan] = useState<Org_Plan_Type_Enum>(org.plan)
  const [endsAt, setEndsAt] = useState(toDateInput(org.planEndsAt))
  const [disabled, setDisabled] = useState(!!org.disabledAt)
  const [whiteboard, setWhiteboard] = useState(org.whiteboardEnabled)

  // Repartir des valeurs de l'organisation à chaque ouverture : la modale est
  // montée une fois par ligne du tableau et resservirait sinon un état périmé.
  useEffect(() => {
    if (!modalProps.isOpen) return
    setPlan(org.plan)
    setEndsAt(toDateInput(org.planEndsAt))
    setDisabled(!!org.disabledAt)
    setWhiteboard(org.whiteboardEnabled)
  }, [
    modalProps.isOpen,
    org.plan,
    org.planEndsAt,
    org.disabledAt,
    org.whiteboardEnabled,
  ])

  const handleSubmit = async () => {
    await updateOrgPlan({
      variables: {
        id: org.id,
        values: {
          plan,
          // 🔑 Vide = pas d'expiration (ADM-D11). On écrit bien `null`, pas une
          // chaîne vide : c'est l'absence de date qui porte le sens « jamais ».
          planEndsAt: endsAt ? new Date(endsAt).toISOString() : null,
          // On ne réécrit pas la date de désactivation si elle existe déjà :
          // elle dit DEPUIS QUAND l'organisation est fermée, et ré-enregistrer
          // le formulaire sans y toucher effacerait cette information.
          disabledAt: disabled
            ? org.disabledAt ?? new Date().toISOString()
            : null,
          // #210 : éteindre cache la vue, le dessin (org_whiteboard) reste.
          whiteboardEnabled: whiteboard,
        },
      },
    })
    onUpdated?.()
    modalProps.onClose()
  }

  return (
    <Modal size="lg" {...modalProps}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('SuperAdmin.orgs.planModal.heading', { org: org.name })}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={5} align="stretch">
            <FormControl>
              <FormLabel>{t('SuperAdmin.orgs.planModal.plan')}</FormLabel>
              <Select
                value={plan}
                onChange={(e) => setPlan(e.target.value as Org_Plan_Type_Enum)}
              >
                {PLAN_ORDER.map((value) => (
                  <option key={value} value={value}>
                    {t(PLAN_LABEL_KEY[value])}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>{t('SuperAdmin.orgs.planModal.endsAt')}</FormLabel>
              <Input
                type="date"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
              />
              <FormHelperText>
                {endsAt
                  ? t('SuperAdmin.orgs.planModal.endsAtSet')
                  : t('SuperAdmin.orgs.planModal.endsAtNever')}
              </FormHelperText>
            </FormControl>

            {/*
              🔴 Le helper et l'alerte DOIVENT rester dans ce FormControl.
              `FormHelperText` lit le contexte du FormControl : posé à côté, il
              fait planter le rendu de toute la modale — c'est le bug remonté par
              Yohan le 16/08, la fenêtre « Changer le plan » ne s'ouvrait pas.
            */}
            <FormControl>
              <HStack>
                <Switch
                  id="org-disabled"
                  isChecked={disabled}
                  onChange={(e) => setDisabled(e.target.checked)}
                />
                <FormLabel htmlFor="org-disabled" mb={0}>
                  {t('SuperAdmin.orgs.planModal.disabled')}
                </FormLabel>
              </HStack>

              <FormHelperText>
                {t('SuperAdmin.orgs.planModal.disabledHelp')}
              </FormHelperText>

              {/*
                Dire ce qui n'est PAS encore vrai. `disabledAt` n'est lu nulle
                part ailleurs dans l'application : la désactivation s'enregistre
                et s'affiche ici, mais les membres continuent d'accéder à tout —
                ce que Yohan a constaté en testant. C'est le verrouillage par
                plan (phase 4) qui l'appliquera.
              */}
              {disabled && (
                <Alert status="warning" fontSize="sm" mt={3}>
                  <AlertIcon />
                  {t('SuperAdmin.orgs.planModal.disabledNotEnforced')}
                </Alert>
              )}
            </FormControl>

            <FormControl>
              <HStack>
                <Switch
                  id="org-whiteboard"
                  isChecked={whiteboard}
                  onChange={(e) => setWhiteboard(e.target.checked)}
                />
                <FormLabel htmlFor="org-whiteboard" mb={0}>
                  {t('SuperAdmin.orgs.planModal.whiteboard')}
                </FormLabel>
              </HStack>
              <FormHelperText>
                {t('SuperAdmin.orgs.planModal.whiteboardHelp')}
              </FormHelperText>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button onClick={modalProps.onClose} mr={3}>
            {t('common.cancel')}
          </Button>
          <Button colorScheme="blue" isLoading={loading} onClick={handleSubmit}>
            {t('common.save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
